import { Link } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { PencilIcon } from 'lucide-react';

import { LinkToExternal } from '@/components/link-to-external';
import { SessionFeedbackStatus } from '@/components/session/session-feedback-status';
import { createTableRowSelectionCell } from '@/components/table/entity-table';
import { formatDateWithDayOfWeek, formatTimeRange, isNotNullish, isStudentRole } from '@airreading/common';
import {
  SessionFeedbackWithAssessment,
  SessionFeedbackIssueDetails,
  studentYearOption,
  StudentYearColorMap,
  toSessionFeedbackCompoundId,
} from '@airreading/common/data';
import { getDistrictColumn } from '@/lib/district-utils';
import { normalizeNewlines, stringIfNull } from '@/lib/string-utils';
import { getAttendanceDisplayValue } from '@/components/feedbacks/session-feedback-utils';
import { CopyableLink } from '@/components/copyable-link';
import { DEFAULT_PAGE_SIZE } from '@/lib/pagination';

const adminCol = createColumnHelper<SessionFeedbackWithAssessment>();

export const FeedbacksTableColumnIds = {
  viewDetailsAdmin: 'viewDetailsAdmin',
  date: 'date',
  time: 'time',
  student: 'student',
  feedbackStatus: 'feedbackStatus',
  attendance: 'attendance',
  district: 'district',
  school: 'school',
  grade: 'grade',
  sessionTeacher: 'sessionTeacher',
  originalTeacher: 'originalTeacher',
  lessonName: 'lessonName',
  dailySummary: 'dailySummary',
  recordingLink: 'recordingLink',
  studentIssues: 'studentIssues',
  majorClassConcerns: 'majorClassConcerns',
  majorConcernsText: 'majorConcernsText',
  assessmentFinished: 'assessmentFinished',
  gradeLevelFluencyWPM: 'gradeLevelFluencyWPM',
  gradeLevelAccuracy: 'gradeLevelAccuracy',
  progressMonitorAccuracy: 'progressMonitorAccuracy',
  progressMonitorWPM: 'progressMonitorWPM',
  sessionIssues: 'sessionIssues',
  sessionFeedbackContent: 'sessionFeedbackContent',
  sessionMajorConcerns: 'sessionMajorConcerns',
} as const;

export type FeedbacksTableColumnId = (typeof FeedbacksTableColumnIds)[keyof typeof FeedbacksTableColumnIds];

export const feedbacksPageColumns = (
  displayTimeZone: string,
  schoolIdToDistrictName: Record<string, string>,
  enableBulkActions: boolean,
  isSelectAllPagesActive?: boolean,
  onSelectAllPagesChange?: (isActive: boolean) => void,
) =>
  [
    enableBulkActions
      ? createTableRowSelectionCell(adminCol, { isSelectAllPagesActive, onSelectAllPagesChange })
      : null,

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.viewDetailsAdmin,
      header: 'Edit',
      cell: ({ getValue }) => {
        return (
          <Link
            to="/sessions/session-feedbacks/$feedbackId"
            params={{ feedbackId: toSessionFeedbackCompoundId(getValue()) }}
            search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
          >
            <PencilIcon size={16} />
          </Link>
        );
      },
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.date,
      header: 'Date (CT)',
      cell: ({ getValue }) => {
        const session = getValue();
        return <div>{formatDateWithDayOfWeek(session.startInstant, displayTimeZone)}</div>;
      },
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.time,
      header: 'Time (CT)',
      cell: ({ getValue }) => {
        const session = getValue();
        return <div>{formatTimeRange(session.startInstant, session.endInstant, displayTimeZone)}</div>;
      },
    }),

    adminCol.accessor('student', {
      id: FeedbacksTableColumnIds.student,
      header: 'Student',
      cell: ({ getValue }) => {
        const student = getValue();
        return <div>{student.fullNameOrEmail}</div>;
      },
    }),

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.feedbackStatus,
      header: 'Feedback status',
      cell: ({ getValue }) => {
        const feedback = getValue();
        const {
          session: { hoursCanceledBeforeStart, lesson },
        } = feedback;
        return (
          <SessionFeedbackStatus
            feedbacks={[feedback]}
            hoursCanceledBeforeStart={hoursCanceledBeforeStart}
            lessonShouldSkipFeedback={lesson.shouldSkipFeedback}
          />
        );
      },
    }),

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.attendance,
      header: 'Student attendance',
      cell: ({ getValue }) => {
        const feedback = getValue();
        return getAttendanceDisplayValue(feedback);
      },
    }),

    adminCol.accessor('student', {
      id: 'school',
      header: 'School',
      cell: ({ getValue }) => {
        const student = getValue();
        const studentRole = student.userRoles.find(isStudentRole);
        const school = studentRole?.school;
        if (!school) return <span>Unknown</span>;
        return (
          <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
            {school.name}
          </Link>
        );
      },
    }),

    adminCol.accessor('student', {
      id: 'district',
      header: 'District',
      cell: ({ getValue }) => {
        const student = getValue();
        const studentRole = student.userRoles.find(isStudentRole);
        const schoolId = studentRole?.school?.id;
        const districtName = schoolId ? schoolIdToDistrictName[schoolId] : null;
        const orgId = studentRole?.school?.orgId;
        if (!districtName || !orgId) return <span>Unknown</span>;
        return getDistrictColumn({
          orgId,
          orgName: districtName,
        });
      },
    }),

    adminCol.accessor('student', {
      id: FeedbacksTableColumnIds.grade,
      header: 'Grade',
      cell: ({ getValue }) => {
        const grade = getValue().studentProfile?.grade;
        if (!grade) return studentYearOption(stringIfNull(grade));
        const backgroundColor = StudentYearColorMap[grade];
        return (
          <div className="min-w-[80px] flex justify-start">
            <span className="text-xs rounded-full px-2 py-1 min-w-9 text-center" style={{ backgroundColor }}>
              {studentYearOption(grade)}
            </span>
          </div>
        );
      },
    }),

    adminCol.accessor('coach', {
      id: FeedbacksTableColumnIds.sessionTeacher,
      header: 'Session teacher',
      cell: ({ getValue }) => {
        const coach = getValue();
        return <div>{coach.fullNameOrEmail}</div>;
      },
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.lessonName,
      header: 'Lesson',
      cell: ({ getValue }) => {
        const { lessonLink, lesson } = getValue();
        if (!lesson) return '—';
        if (!lessonLink) return <div className="flex items-center gap-2">{lesson.name}</div>;

        return (
          <CopyableLink
            externalHref={lessonLink}
            className="text-blue-600 hover:text-blue-800 underline"
            showExternalHrefIcon={false}
          >
            {lesson.name}
          </CopyableLink>
        );
      },
    }),

    adminCol.accessor((row) => row.session.zoom, {
      id: FeedbacksTableColumnIds.recordingLink,
      header: 'Recording',
      cell: ({ getValue }) => {
        const sessionZoom = getValue();
        const recordingUrls = sessionZoom?.recordingUrls ?? [];
        if (recordingUrls.length === 0) {
          return <div>-</div>;
        }

        return (
          <div className="flex flex-col gap-1">
            {recordingUrls.map((url, index) => (
              <LinkToExternal
                key={`${url}-${index}`}
                href={url}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                {recordingUrls.length > 1 ? `Recording ${index + 1}` : 'View Recording'}
              </LinkToExternal>
            ))}
          </div>
        );
      },
    }),

    adminCol.accessor((row) => row.issues, {
      id: FeedbacksTableColumnIds.sessionIssues,
      header: 'Student issues',
      cell: ({ getValue }) => {
        const issues = getValue();
        if (!issues || issues.length === 0) {
          return <div>-</div>;
        }

        const readableIssues = issues.map((issue) => SessionFeedbackIssueDetails[issue]?.label || issue);

        return (
          <div className="text-sm max-w-[500px] break-words whitespace-normal overflow-wrap-anywhere">
            {readableIssues.join(', ')}
          </div>
        );
      },
    }),

    adminCol.accessor((row) => row.majorConcerns, {
      id: FeedbacksTableColumnIds.sessionMajorConcerns,
      header: 'Major student issue - Teacher report',
      cell: ({ getValue }) => {
        const majorConcerns = getValue();
        if (!majorConcerns) {
          return <div>-</div>;
        }

        return (
          <div className="max-w-[500px] break-words whitespace-normal overflow-wrap-anywhere" title={majorConcerns}>
            {majorConcerns}
          </div>
        );
      },
    }),

    adminCol.accessor((row) => row.feedbackContent, {
      id: FeedbacksTableColumnIds.sessionFeedbackContent,
      header: 'Class notes',
      cell: ({ getValue }) => {
        const feedbackContent = getValue();
        if (!feedbackContent) {
          return <div>-</div>;
        }

        return (
          <div className="max-w-[500px] break-words whitespace-normal overflow-wrap-anywhere" title={feedbackContent}>
            {feedbackContent}
          </div>
        );
      },
    }),

    adminCol.accessor((row) => row.progressMonitorWPM, {
      id: FeedbacksTableColumnIds.progressMonitorWPM,
      header: 'Reading Level Fluency (WPM)',
      cell: ({ getValue }) => {
        const wpm = getValue();
        return <div>{wpm !== null ? wpm.toFixed(2) : '-'}</div>;
      },
    }),

    adminCol.accessor((row) => row.progressMonitorAccuracy, {
      id: FeedbacksTableColumnIds.progressMonitorAccuracy,
      header: 'Reading Level Accuracy',
      cell: ({ getValue }) => {
        const accuracy = getValue();
        return <div>{accuracy !== null ? `${accuracy.toFixed(2)}%` : '-'}</div>;
      },
    }),

    adminCol.accessor((row) => row.gradeLevelWPM, {
      id: FeedbacksTableColumnIds.gradeLevelFluencyWPM,
      header: 'Grade Level Fluency (WPM)',
      cell: ({ getValue }) => {
        const wpm = getValue();
        return <div>{wpm !== null ? wpm.toFixed(2) : '-'}</div>;
      },
    }),

    adminCol.accessor((row) => row.gradeLevelAccuracy, {
      id: FeedbacksTableColumnIds.gradeLevelAccuracy,
      header: 'Grade Level Accuracy',
      cell: ({ getValue }) => {
        const accuracy = getValue();
        return <div>{accuracy !== null ? `${accuracy.toFixed(2)}%` : '-'}</div>;
      },
    }),
  ].filter(isNotNullish);

export const sessionFeedbacksPageColumns = (
  displayTimeZone: string,
  schoolIdToDistrictName: Record<string, string>,
  enableBulkActions: boolean,
  isSelectAllPagesActive?: boolean,
  onSelectAllPagesChange?: (isActive: boolean) => void,
) =>
  [
    enableBulkActions
      ? createTableRowSelectionCell(adminCol, { isSelectAllPagesActive, onSelectAllPagesChange })
      : null,

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.date,
      header: 'Date (CT)',
      cell: ({ getValue }) => {
        const session = getValue();
        return <div>{formatDateWithDayOfWeek(session.startInstant, displayTimeZone)}</div>;
      },
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.time,
      header: 'Time (CT)',
      cell: ({ getValue }) => {
        const session = getValue();
        return <div>{formatTimeRange(session.startInstant, session.endInstant, displayTimeZone)}</div>;
      },
    }),

    adminCol.accessor('student', {
      id: FeedbacksTableColumnIds.student,
      header: 'Student',
      cell: ({ getValue }) => {
        const student = getValue();
        return <div>{student.fullNameOrEmail}</div>;
      },
    }),

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.feedbackStatus,
      header: 'Feedback status',
      cell: ({ getValue }) => {
        const feedback = getValue();
        const {
          session: { hoursCanceledBeforeStart, lesson },
        } = feedback;
        return (
          <SessionFeedbackStatus
            feedbacks={[feedback]}
            hoursCanceledBeforeStart={hoursCanceledBeforeStart}
            lessonShouldSkipFeedback={lesson.shouldSkipFeedback}
          />
        );
      },
    }),

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.attendance,
      header: 'Student attendance',
      cell: ({ getValue }) => {
        const feedback = getValue();
        return getAttendanceDisplayValue(feedback);
      },
    }),

    adminCol.accessor('student', {
      id: 'school',
      header: 'School',
      cell: ({ getValue }) => {
        const student = getValue();
        const studentRole = student.userRoles.find(isStudentRole);
        const school = studentRole?.school;
        if (!school) return <span>Unknown</span>;
        return (
          <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
            {school.name}
          </Link>
        );
      },
    }),

    adminCol.accessor('student', {
      id: 'district',
      header: 'District',
      cell: ({ getValue }) => {
        const student = getValue();
        const studentRole = student.userRoles.find(isStudentRole);
        const schoolId = studentRole?.school?.id;
        const districtName = schoolId ? schoolIdToDistrictName[schoolId] : null;
        if (!districtName) return <span>Unknown</span>;
        return getDistrictColumn({
          orgName: districtName,
          shouldHaveLink: false,
        });
      },
    }),

    adminCol.accessor('student', {
      id: FeedbacksTableColumnIds.grade,
      header: 'Grade',
      cell: ({ getValue }) => {
        const grade = getValue().studentProfile?.grade;
        if (!grade) return studentYearOption(stringIfNull(grade));
        const backgroundColor = StudentYearColorMap[grade];
        return (
          <div className="min-w-[80px] flex justify-start">
            <span className="text-xs rounded-full px-2 py-1 min-w-9 text-center" style={{ backgroundColor }}>
              {studentYearOption(grade)}
            </span>
          </div>
        );
      },
    }),

    adminCol.accessor('coach', {
      id: FeedbacksTableColumnIds.sessionTeacher,
      header: 'Session teacher',
      cell: ({ getValue }) => {
        const coach = getValue();
        return <div>{coach.fullNameOrEmail}</div>;
      },
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.lessonName,
      header: 'Lesson',
      cell: ({ getValue }) => {
        const { lessonLink, lesson } = getValue();
        if (!lesson) return '—';
        if (!lessonLink) return <div className="flex items-center gap-2">{lesson.name}</div>;

        return (
          <CopyableLink
            externalHref={lessonLink}
            className="text-blue-600 hover:text-blue-800 underline"
            showExternalHrefIcon={false}
          >
            {lesson.name}
          </CopyableLink>
        );
      },
    }),

    adminCol.accessor((row) => row.session.zoom, {
      id: FeedbacksTableColumnIds.recordingLink,
      header: 'Recording',
      cell: ({ getValue }) => {
        const sessionZoom = getValue();
        const recordingUrls = sessionZoom?.recordingUrls ?? [];
        if (recordingUrls.length === 0) {
          return <div>-</div>;
        }

        return (
          <div className="flex flex-col gap-1">
            {recordingUrls.map((url, index) => (
              <LinkToExternal
                key={`${url}-${index}`}
                href={url}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                {recordingUrls.length > 1 ? `Recording ${index + 1}` : 'View Recording'}
              </LinkToExternal>
            ))}
          </div>
        );
      },
    }),

    adminCol.accessor((row) => row.progressMonitorWPM, {
      id: FeedbacksTableColumnIds.progressMonitorWPM,
      header: 'Reading Level Fluency (WPM)',
      cell: ({ getValue }) => {
        const wpm = getValue();
        return <div>{wpm !== null ? wpm.toFixed(2) : '-'}</div>;
      },
    }),

    adminCol.accessor((row) => row.progressMonitorAccuracy, {
      id: FeedbacksTableColumnIds.progressMonitorAccuracy,
      header: 'Reading Level Accuracy',
      cell: ({ getValue }) => {
        const accuracy = getValue();
        return <div>{accuracy !== null ? `${accuracy.toFixed(2)}%` : '-'}</div>;
      },
    }),

    adminCol.accessor((row) => row.gradeLevelWPM, {
      id: FeedbacksTableColumnIds.gradeLevelFluencyWPM,
      header: 'Grade Level Fluency (WPM)',
      cell: ({ getValue }) => {
        const wpm = getValue();
        return <div>{wpm !== null ? wpm.toFixed(2) : '-'}</div>;
      },
    }),

    adminCol.accessor((row) => row.gradeLevelAccuracy, {
      id: FeedbacksTableColumnIds.gradeLevelAccuracy,
      header: 'Grade Level Accuracy',
      cell: ({ getValue }) => {
        const accuracy = getValue();
        return <div>{accuracy !== null ? `${accuracy.toFixed(2)}%` : '-'}</div>;
      },
    }),
  ].filter(isNotNullish);

export const teacherFeedbacksPageColumns = (
  displayTimeZone: string,
  schoolIdToDistrictName: Record<string, string>,
  enableBulkActions: boolean,
  isSelectAllPagesActive?: boolean,
  onSelectAllPagesChange?: (isActive: boolean) => void,
) =>
  [
    enableBulkActions
      ? createTableRowSelectionCell(adminCol, { isSelectAllPagesActive, onSelectAllPagesChange })
      : null,

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.date,
      header: 'Date',
      cell: ({ getValue }) => {
        const session = getValue();
        return <div>{formatDateWithDayOfWeek(session.startInstant, displayTimeZone)}</div>;
      },
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.time,
      header: 'Time (CT)',
      cell: ({ getValue }) => {
        const session = getValue();
        return <div>{formatTimeRange(session.startInstant, session.endInstant, displayTimeZone)}</div>;
      },
    }),

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.feedbackStatus,
      header: 'Feedback status',
      cell: ({ getValue }) => {
        const feedback = getValue();
        const {
          session: { hoursCanceledBeforeStart, lesson },
        } = feedback;
        return (
          <SessionFeedbackStatus
            feedbacks={[feedback]}
            hoursCanceledBeforeStart={hoursCanceledBeforeStart}
            lessonShouldSkipFeedback={lesson.shouldSkipFeedback}
          />
        );
      },
    }),

    adminCol.accessor('student', {
      id: FeedbacksTableColumnIds.student,
      header: 'Student',
      cell: ({ getValue }) => {
        const student = getValue();
        return <div>{student.fullNameOrEmail}</div>;
      },
    }),

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.attendance,
      header: 'Student attendance',
      cell: ({ getValue }) => {
        const feedback = getValue();
        return getAttendanceDisplayValue(feedback);
      },
    }),

    adminCol.accessor('student', {
      id: 'school',
      header: 'School - District',
      cell: ({ getValue }) => {
        const student = getValue();
        const studentRole = student.userRoles.find(isStudentRole);
        const school = studentRole?.school;
        if (!school) return <span>Unknown - Unknown</span>;
        const schoolId = school.id;
        const districtName = schoolIdToDistrictName[schoolId];
        return (
          <div>
            <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
              {school.name} - <span className="text-blue-600">{districtName}</span>
            </Link>
          </div>
        );
      },
    }),

    adminCol.accessor('student', {
      id: FeedbacksTableColumnIds.grade,
      header: 'Grade',
      cell: ({ getValue }) => {
        const grade = getValue().studentProfile?.grade;
        if (!grade) return studentYearOption(stringIfNull(grade));
        const backgroundColor = StudentYearColorMap[grade];
        return (
          <div className="min-w-[80px] flex justify-start">
            <span className="text-xs rounded-full px-2 py-1 min-w-9 text-center" style={{ backgroundColor }}>
              {studentYearOption(grade)}
            </span>
          </div>
        );
      },
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.lessonName,
      header: 'Lesson',
      cell: ({ getValue }) => {
        const { lessonLink, lesson } = getValue();
        if (!lesson) return '—';
        if (!lessonLink) return <div className="flex items-center gap-2">{lesson.name}</div>;

        return (
          <CopyableLink
            externalHref={lessonLink}
            className="text-blue-600 hover:text-blue-800 underline"
            showExternalHrefIcon={false}
          >
            {lesson.name}
          </CopyableLink>
        );
      },
    }),

    adminCol.accessor((row) => row.progressMonitorWPM, {
      id: FeedbacksTableColumnIds.progressMonitorWPM,
      header: 'Reading Level Fluency (WPM)',
      cell: ({ getValue }) => {
        const wpm = getValue();
        return <div>{wpm !== null ? wpm.toFixed(2) : '-'}</div>;
      },
    }),

    adminCol.accessor((row) => row.progressMonitorAccuracy, {
      id: FeedbacksTableColumnIds.progressMonitorAccuracy,
      header: 'Reading Level Accuracy',
      cell: ({ getValue }) => {
        const accuracy = getValue();
        return <div>{accuracy !== null ? `${accuracy.toFixed(2)}%` : '-'}</div>;
      },
    }),

    adminCol.accessor((row) => row.gradeLevelWPM, {
      id: FeedbacksTableColumnIds.gradeLevelFluencyWPM,
      header: 'Grade Level Fluency (WPM)',
      cell: ({ getValue }) => {
        const wpm = getValue();
        return <div>{wpm !== null ? wpm.toFixed(2) : '-'}</div>;
      },
    }),

    adminCol.accessor((row) => row.gradeLevelAccuracy, {
      id: FeedbacksTableColumnIds.gradeLevelAccuracy,
      header: 'Grade Level Accuracy',
      cell: ({ getValue }) => {
        const accuracy = getValue();
        return <div>{accuracy !== null ? `${accuracy.toFixed(2)}%` : '-'}</div>;
      },
    }),
  ].filter(isNotNullish);

export const studentFeedbacksPageColumns = (
  displayTimeZone: string,
  schoolIdToDistrictName: Record<string, string>,
  enableBulkActions: boolean,
  isSelectAllPagesActive?: boolean,
  onSelectAllPagesChange?: (isActive: boolean) => void,
) =>
  [
    enableBulkActions
      ? createTableRowSelectionCell(adminCol, { isSelectAllPagesActive, onSelectAllPagesChange })
      : null,

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.viewDetailsAdmin,
      header: 'Edit',
      cell: ({ getValue }) => {
        return (
          <Link
            to="/sessions/session-feedbacks/$feedbackId"
            params={{ feedbackId: toSessionFeedbackCompoundId(getValue()) }}
            search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
          >
            <PencilIcon size={16} />
          </Link>
        );
      },
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.date,
      header: 'Date (CT)',
      cell: ({ getValue }) => {
        const session = getValue();
        return <div>{formatDateWithDayOfWeek(session.startInstant, displayTimeZone)}</div>;
      },
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.time,
      header: 'Time (CT)',
      cell: ({ getValue }) => {
        const session = getValue();
        return <div>{formatTimeRange(session.startInstant, session.endInstant, displayTimeZone)}</div>;
      },
    }),

    adminCol.accessor('student', {
      id: FeedbacksTableColumnIds.student,
      header: 'Student',
      cell: ({ getValue }) => {
        const student = getValue();
        return <div>{student.fullNameOrEmail}</div>;
      },
    }),

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.feedbackStatus,
      header: 'Feedback status',
      cell: ({ getValue }) => {
        const feedback = getValue();
        const {
          session: { hoursCanceledBeforeStart, lesson },
        } = feedback;
        return (
          <SessionFeedbackStatus
            feedbacks={[feedback]}
            hoursCanceledBeforeStart={hoursCanceledBeforeStart}
            lessonShouldSkipFeedback={lesson.shouldSkipFeedback}
          />
        );
      },
    }),

    adminCol.accessor((row) => row, {
      id: FeedbacksTableColumnIds.attendance,
      header: 'Student attendance',
      cell: ({ getValue }) => {
        const feedback = getValue();
        return getAttendanceDisplayValue(feedback);
      },
    }),

    adminCol.accessor('student', {
      id: 'school',
      header: 'School',
      cell: ({ getValue }) => {
        const student = getValue();
        const studentRole = student.userRoles.find(isStudentRole);
        const school = studentRole?.school;
        if (!school) return <span>Unknown</span>;
        return (
          <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
            {school.name}
          </Link>
        );
      },
    }),

    adminCol.accessor('student', {
      id: 'district',
      header: 'District',
      cell: ({ getValue }) => {
        const student = getValue();
        const studentRole = student.userRoles.find(isStudentRole);
        const schoolId = studentRole?.school?.id;
        const districtName = schoolId ? schoolIdToDistrictName[schoolId] : null;
        const orgId = studentRole?.school?.orgId;
        if (!districtName || !orgId) return <span>Unknown</span>;
        return getDistrictColumn({
          orgId,
          orgName: districtName,
        });
      },
    }),

    adminCol.accessor('student', {
      id: FeedbacksTableColumnIds.grade,
      header: 'Grade',
      cell: ({ getValue }) => {
        const grade = getValue().studentProfile?.grade;
        if (!grade) return studentYearOption(stringIfNull(grade));
        const backgroundColor = StudentYearColorMap[grade];
        return (
          <div className="min-w-[80px] flex justify-start">
            <span className="text-xs rounded-full px-2 py-1 min-w-9 text-center" style={{ backgroundColor }}>
              {studentYearOption(grade)}
            </span>
          </div>
        );
      },
    }),

    adminCol.accessor('coach', {
      id: FeedbacksTableColumnIds.sessionTeacher,
      header: 'Session teacher',
      cell: ({ getValue }) => {
        const coach = getValue();
        return <div>{coach.fullNameOrEmail}</div>;
      },
    }),

    adminCol.accessor('session', {
      id: FeedbacksTableColumnIds.lessonName,
      header: 'Lesson',
      cell: ({ getValue }) => {
        const { lessonLink, lesson } = getValue();
        if (!lesson) return '—';
        if (!lessonLink) return <div className="flex items-center gap-2">{lesson.name}</div>;

        return (
          <CopyableLink
            externalHref={lessonLink}
            className="text-blue-600 hover:text-blue-800 underline"
            showExternalHrefIcon={false}
          >
            {lesson.name}
          </CopyableLink>
        );
      },
    }),

    adminCol.accessor((row) => row.dailySummary, {
      id: FeedbacksTableColumnIds.dailySummary,
      header: 'Daily Summary',
      cell: ({ getValue }) => {
        const dailySummary = getValue();
        return <div className="whitespace-pre-line">{dailySummary ? normalizeNewlines(dailySummary) : '-'}</div>;
      },
    }),

    adminCol.accessor((row) => row.session.zoom, {
      id: FeedbacksTableColumnIds.recordingLink,
      header: 'Recording',
      cell: ({ getValue }) => {
        const sessionZoom = getValue();
        const recordingUrls = sessionZoom?.recordingUrls ?? [];
        if (recordingUrls.length === 0) {
          return <div>-</div>;
        }

        return (
          <div className="flex flex-col gap-1">
            {recordingUrls.map((url, index) => (
              <LinkToExternal
                key={`${url}-${index}`}
                href={url}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                {recordingUrls.length > 1 ? `Recording ${index + 1}` : 'View Recording'}
              </LinkToExternal>
            ))}
          </div>
        );
      },
    }),

    adminCol.accessor((row) => row.issues, {
      id: FeedbacksTableColumnIds.sessionIssues,
      header: 'Student issues',
      cell: ({ getValue }) => {
        const issues = getValue();
        if (!issues || issues.length === 0) {
          return <div>-</div>;
        }

        const readableIssues = issues.map((issue) => SessionFeedbackIssueDetails[issue]?.label || issue);

        return (
          <div className="text-sm max-w-[500px] break-words whitespace-normal overflow-wrap-anywhere">
            {readableIssues.join(', ')}
          </div>
        );
      },
    }),

    adminCol.accessor((row) => row.majorConcerns, {
      id: FeedbacksTableColumnIds.sessionMajorConcerns,
      header: 'Major student issue - Teacher report',
      cell: ({ getValue }) => {
        const majorConcerns = getValue();
        if (!majorConcerns) {
          return <div>-</div>;
        }

        return (
          <div className="max-w-[500px] break-words whitespace-normal overflow-wrap-anywhere" title={majorConcerns}>
            {majorConcerns}
          </div>
        );
      },
    }),

    adminCol.accessor((row) => row.feedbackContent, {
      id: FeedbacksTableColumnIds.sessionFeedbackContent,
      header: 'Class notes',
      cell: ({ getValue }) => {
        const feedbackContent = getValue();
        if (!feedbackContent) {
          return <div>-</div>;
        }

        return (
          <div className="max-w-[500px] break-words whitespace-normal overflow-wrap-anywhere" title={feedbackContent}>
            {feedbackContent}
          </div>
        );
      },
    }),

    adminCol.accessor((row) => row.progressMonitorWPM, {
      id: FeedbacksTableColumnIds.progressMonitorWPM,
      header: 'Reading Level Fluency (WPM)',
      cell: ({ getValue }) => {
        const wpm = getValue();
        return <div>{wpm !== null ? wpm.toFixed(2) : '-'}</div>;
      },
    }),

    adminCol.accessor((row) => row.progressMonitorAccuracy, {
      id: FeedbacksTableColumnIds.progressMonitorAccuracy,
      header: 'Reading Level Accuracy',
      cell: ({ getValue }) => {
        const accuracy = getValue();
        return <div>{accuracy !== null ? `${accuracy.toFixed(2)}%` : '-'}</div>;
      },
    }),

    adminCol.accessor((row) => row.gradeLevelWPM, {
      id: FeedbacksTableColumnIds.gradeLevelFluencyWPM,
      header: 'Grade Level Fluency (WPM)',
      cell: ({ getValue }) => {
        const wpm = getValue();
        return <div>{wpm !== null ? wpm.toFixed(2) : '-'}</div>;
      },
    }),

    adminCol.accessor((row) => row.gradeLevelAccuracy, {
      id: FeedbacksTableColumnIds.gradeLevelAccuracy,
      header: 'Grade Level Accuracy',
      cell: ({ getValue }) => {
        const accuracy = getValue();
        return <div>{accuracy !== null ? `${accuracy.toFixed(2)}%` : '-'}</div>;
      },
    }),
  ].filter(isNotNullish);
